import { useRef, useEffect } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import type { OrbitControls as OrbitControlsType } from 'three-stdlib';

interface CameraControllerProps {
  /** Enable orbit controls (user can rotate view) */
  enableControls?: boolean;
  /** Auto rotate the camera */
  autoRotate?: boolean;
  /** Initial camera position */
  position?: [number, number, number];
  /** Camera look-at target */
  target?: [number, number, number];
  /** Minimum distance for zoom */
  minDistance?: number;
  /** Maximum distance for zoom */
  maxDistance?: number;
  /** Enable zoom */
  enableZoom?: boolean;
  /** Enable pan */
  enablePan?: boolean;
}

/**
 * Camera controller with optional orbit controls
 */
export function CameraController({
  enableControls = false,
  autoRotate = false,
  position = [0, 2, 8],
  target = [0, 0, 0],
  minDistance = 5,
  maxDistance = 15,
  enableZoom = false,
  enablePan = false,
}: CameraControllerProps) {
  const controlsRef = useRef<OrbitControlsType>(null);
  const { camera } = useThree();

  // Set initial camera position
  useEffect(() => {
    camera.position.set(...position);
    camera.lookAt(...target);
  }, [camera, position, target]);

  if (enableControls) {
    return (
      <OrbitControls
        ref={controlsRef}
        target={target}
        minDistance={minDistance}
        maxDistance={maxDistance}
        enableZoom={enableZoom}
        enablePan={enablePan}
        autoRotate={autoRotate}
        autoRotateSpeed={0.5}
        // Limit vertical rotation
        maxPolarAngle={Math.PI / 2}
        minPolarAngle={Math.PI / 4}
      />
    );
  }

  return null;
}

interface CameraShakeProps {
  /** Shake intensity */
  intensity?: number;
  /** Shake duration in ms */
  duration?: number;
  /** Whether shake is active */
  active?: boolean;
}

/**
 * Camera shake effect for wins/events
 */
export function CameraShake({
  intensity = 0.1,
  duration = 500,
  active = false,
}: CameraShakeProps) {
  const { camera } = useThree();
  const originalPosition = useRef(new THREE.Vector3());
  const startTime = useRef(0);
  const isShaking = useRef(false);

  useEffect(() => {
    if (active && !isShaking.current) {
      originalPosition.current.copy(camera.position);
      startTime.current = Date.now();
      isShaking.current = true;
    }
  }, [active, camera]);

  useFrame(() => {
    if (!isShaking.current) return;

    const elapsed = Date.now() - startTime.current;
    const progress = elapsed / duration;

    if (progress >= 1) {
      // Reset to original position
      camera.position.copy(originalPosition.current);
      isShaking.current = false;
      return;
    }

    // Decay the shake intensity over time
    const decay = 1 - progress;
    const currentIntensity = intensity * decay;

    // Apply random shake offset using a mutable approach
    const newX = originalPosition.current.x + (Math.random() - 0.5) * currentIntensity;
    const newY = originalPosition.current.y + (Math.random() - 0.5) * currentIntensity;
    camera.position.set(newX, newY, camera.position.z);
  });

  return null;
}

interface CameraZoomProps {
  /** Target zoom level (distance from target) */
  targetZoom?: number;
  /** Zoom animation duration */
  duration?: number;
  /** Whether to animate the zoom */
  animate?: boolean;
}

/**
 * Camera zoom effect for focusing on wins
 */
export function CameraZoom({
  targetZoom = 8,
  duration = 500,
  animate = false,
}: CameraZoomProps) {
  const { camera } = useThree();
  const startDistance = useRef(0);
  const startTime = useRef(0);
  const isZooming = useRef(false);

  useEffect(() => {
    if (animate && !isZooming.current) {
      startDistance.current = camera.position.length();
      startTime.current = Date.now();
      isZooming.current = true;
    }
  }, [animate, camera]);

  useFrame(() => {
    if (!isZooming.current) return;

    const elapsed = Date.now() - startTime.current;
    const progress = Math.min(elapsed / duration, 1);

    // Ease out cubic
    const eased = 1 - Math.pow(1 - progress, 3);

    // Interpolate distance
    const currentDistance =
      startDistance.current + (targetZoom - startDistance.current) * eased;

    // Maintain direction, adjust distance
    const direction = camera.position.clone().normalize();
    camera.position.copy(direction.multiplyScalar(currentDistance));

    if (progress >= 1) {
      isZooming.current = false;
    }
  });

  return null;
}
